import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { PartnerGoalTarget } from "@colab/shared";

export type RefreshGoalTargetInput = {
  id: string;
  /** Goal + partner this target belongs to (cache invalidation only). */
  goalId: string;
  partnerId: string;
};

type RefreshTargetResponse = {
  ok: true;
  target: PartnerGoalTarget;
  currentValue: number;
  status: string;
};

// POST /api/admin/goals/targets/:id/refresh
//
// Re-computes current_value + status. Honors current_value_override when set
// (no aggregation re-run; just recomputes status against override + period).
// Otherwise runs `computeGoalSpec` against the goal's full spec.
export function useRefreshGoalTarget() {
  const queryClient = useQueryClient();
  return useMutation<RefreshTargetResponse, ApiClientError, RefreshGoalTargetInput>({
    mutationFn: ({ id }) =>
      apiFetch<RefreshTargetResponse>(`/admin/goals/targets/${id}/refresh`, {
        method: "POST",
      }),
    onSuccess: (_data, { goalId, partnerId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.goals.targetsForGoal(goalId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.goals.detail(goalId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.goals.forPartner(partnerId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.partners.detail(partnerId),
      });
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to refresh target");
    },
  });
}
